function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef } from "react";
import { cva } from "class-variance-authority";
import { Typography } from "../Typography/index.js";
import { cn } from "../../common/helpers.js";
const classesRoot = cva([
    'group',
    'flex',
    'flex-col',
    'gap-4',
    'md:flex-row',
    'md:gap-8',
    'w-full',
    'rounded-xl',
    'md:shadow',
    'md:bg-gray-50',
    'duration-200',
    'transition-shadow'
], {
    variants: {
        noAction: {
            true: [],
            false: [
                'md:hover:shadow-md',
                'outline-2',
                'outline-offset-2',
                'outline-primary'
            ]
        }
    }
});
/*
 * Note: Width full and aspect ratio together will prevent content layout shift.
 * Object cover seemed like a sensible default; but can be overriden with slot props.
 */ const classesImage = cva([
    'scale-100',
    'duration-200',
    'w-full',
    'object-cover',
    'origin-center',
    'duration-200',
    'aspect-4/3',
    'md:min-w-[286px]',
    'md:w-[286px]'
], {
    variants: {
        noAction: {
            true: [],
            false: [
                'group-hover:scale-105'
            ]
        }
    }
});
/**
 * Card Image Long - [API](https://scl.sharp.com/?path=/docs/components-cardimagelong) | [DSM](https://sharphealthcare.notion.site/Card-image-long-item-8d1fb560419d4fa6a31999385eb9259a)
 *
 * @example
 *
 * <CardImageLong
 *  heading="Example 1"
 *  description="With custom image component"
 *  image={{ src: 'image.jpg' }}
 *  href="/" />
 *
 * <CardImageLong
 *  heading="Example 2"
 *  description="With no action"
 *  image={{ src: 'image.jpg' }}
 *  noAction />
 */ const CardImageLong = /*#__PURE__*/ forwardRef((props, ref)=>{
    var _slotProps_preHeading, _slotProps_description;
    const { as, preHeading, heading, description, image = {}, noAction = false, slots = {}, slotProps = {}, className } = props, rest = _object_without_properties(props, [
        "as",
        "preHeading",
        "heading",
        "description",
        "image",
        "noAction",
        "slots",
        "slotProps",
        "className"
    ]);
    const Root = as !== null && as !== void 0 ? as : noAction ? 'div' : 'a';
    var _slots_imageContainer;
    const ImageContainer = (_slots_imageContainer = slots.imageContainer) !== null && _slots_imageContainer !== void 0 ? _slots_imageContainer : 'div';
    var _slotProps_imageContainer;
    const _ref = (_slotProps_imageContainer = slotProps === null || slotProps === void 0 ? void 0 : slotProps.imageContainer) !== null && _slotProps_imageContainer !== void 0 ? _slotProps_imageContainer : {}, { className: imageContainerSlotPropsClassName } = _ref, imageContainerSlotProps = _object_without_properties(_ref, [
        "className"
    ]);
    var _slots_image;
    const Image = (_slots_image = slots.image) !== null && _slots_image !== void 0 ? _slots_image : 'img';
    const { className: imagePropClassName } = image, imagePropProps = _object_without_properties(image, [
        "className"
    ]);
    var _slotProps_image;
    const _ref1 = (_slotProps_image = slotProps.image) !== null && _slotProps_image !== void 0 ? _slotProps_image : {}, { className: imageSlotPropsClassName } = _ref1, imageSlotProps = _object_without_properties(_ref1, [
        "className"
    ]);
    const imageProps = slots.image ? imageSlotProps : _object_spread({}, imagePropProps, imageSlotProps);
    var _slots_contentContainer;
    const ContentContainer = (_slots_contentContainer = slots.contentContainer) !== null && _slots_contentContainer !== void 0 ? _slots_contentContainer : 'div';
    var _slotProps_contentContainer;
    const _ref2 = (_slotProps_contentContainer = slotProps.contentContainer) !== null && _slotProps_contentContainer !== void 0 ? _slotProps_contentContainer : {}, { className: contentContainerSlotPropsClassName } = _ref2, contentContainerSlotProps = _object_without_properties(_ref2, [
        "className"
    ]);
    var _slots_preHeading;
    const PreHeading = (_slots_preHeading = slots.preHeading) !== null && _slots_preHeading !== void 0 ? _slots_preHeading : Typography;
    var _slotProps_preHeading1;
    const _ref3 = (_slotProps_preHeading1 = slotProps.preHeading) !== null && _slotProps_preHeading1 !== void 0 ? _slotProps_preHeading1 : {}, { className: preHeadingSlotPropsClassName } = _ref3, preHeadingSlotProps = _object_without_properties(_ref3, [
        "className"
    ]);
    // If preHeading slot provided then do not spread Typography specific props else combine
    const preHeadingProps = slots.preHeading ? preHeadingSlotProps : _object_spread({
        variant: 'small-body-semibold'
    }, preHeadingSlotProps);
    var _slots_heading;
    const Heading = (_slots_heading = slots.heading) !== null && _slots_heading !== void 0 ? _slots_heading : Typography;
    var _slotProps_heading;
    const _ref4 = (_slotProps_heading = slotProps.heading) !== null && _slotProps_heading !== void 0 ? _slotProps_heading : {}, { className: headingSlotPropsClassName } = _ref4, headingSlotProps = _object_without_properties(_ref4, [
        "className"
    ]);
    // If heading slot provided then do not spread Typography specific props else combine
    const headingProps = slots.heading ? headingSlotProps : _object_spread({
        variant: 'h3'
    }, headingSlotProps);
    var _slots_description;
    const Description = (_slots_description = slots.description) !== null && _slots_description !== void 0 ? _slots_description : Typography;
    var _slotProps_description1;
    const _ref5 = (_slotProps_description1 = slotProps.description) !== null && _slotProps_description1 !== void 0 ? _slotProps_description1 : {}, { className: descriptionSlotPropsClassName } = _ref5, descriptionSlotProps = _object_without_properties(_ref5, [
        "className"
    ]);
    // If description slot provided then do not spread Typography specific props else combine
    const descriptionProps = slots.description ? descriptionSlotProps : _object_spread({
        variant: 'small-body'
    }, descriptionSlotProps);
    const hasPreHeading = preHeading !== null && preHeading !== void 0 ? preHeading : (_slotProps_preHeading = slotProps.preHeading) === null || _slotProps_preHeading === void 0 ? void 0 : _slotProps_preHeading.children;
    const hasDescription = description !== null && description !== void 0 ? description : (_slotProps_description = slotProps.description) === null || _slotProps_description === void 0 ? void 0 : _slotProps_description.children;
    return /*#__PURE__*/ _jsxs(Root, _object_spread_props(_object_spread({}, rest), {
        ref: ref,
        className: cn(classesRoot({
            noAction
        }), className),
        children: [
            /*#__PURE__*/ _jsx(ImageContainer, _object_spread_props(_object_spread({
                className: cn('rounded-xl md:rounded-r-none overflow-hidden flex-shrink-0', imageContainerSlotPropsClassName)
            }, imageContainerSlotProps), {
                children: /*#__PURE__*/ _jsx(Image, _object_spread({
                    className: cn(classesImage({
                        noAction
                    }), imageSlotPropsClassName)
                }, imageProps))
            })),
            /*#__PURE__*/ _jsxs(ContentContainer, _object_spread_props(_object_spread({
                className: cn('flex flex-col gap-2 justify-center md:pr-8', contentContainerSlotPropsClassName)
            }, contentContainerSlotProps), {
                children: [
                    hasPreHeading && /*#__PURE__*/ _jsx(PreHeading, _object_spread_props(_object_spread({
                        className: cn('text-left md:line-clamp-1', preHeadingSlotPropsClassName)
                    }, preHeadingProps), {
                        children: preHeading
                    })),
                    /*#__PURE__*/ _jsx(Heading, _object_spread_props(_object_spread({
                        className: cn('text-left md:line-clamp-2', !noAction && 'group-hover:underline', headingSlotPropsClassName)
                    }, headingProps), {
                        children: heading
                    })),
                    hasDescription && /*#__PURE__*/ _jsx(Description, _object_spread_props(_object_spread({
                        className: cn('text-left', hasPreHeading && 'md:line-clamp-3', !hasPreHeading && 'md:line-clamp-4', descriptionSlotPropsClassName)
                    }, descriptionProps), {
                        children: description
                    }))
                ]
            }))
        ]
    }));
});
export { CardImageLong };
